// ABJ demo clone of src/components/washULivingWellCenter/living_well_center-6-1-pre-surgical.js (6.0 Pre-Surgical, listed under Care at Home 4.0).
// Copied verbatim 10/14/26 — copy and header colors pending from Jeff. Route: /abj-demo-lwc-pre-surgical-exercises
import React from "react";
import Container from "react-bootstrap/Container";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { HashLink as Link } from "react-router-hash-link";
import LegoL99AData from "../legosRefactored-24/dataDriven/LegoL99AData";
import mindfulnessAppsCards from "../legosRefactored-24/dataDriven/mindfulnessAppsCards";
import { pdf2026, thumb2026 } from "../legosRefactored-24/dataDriven/assets";

// Final S3 assets per Jim's TO-1717 Jira comment (uploaded 9/23, all URLs 200).
// Joint and spinal are named explicitly in the filenames — the PDFs themselves
// don't say which patient group they're for, so keep them in their own section.
const JOINT = "Pre-Surgical Joint Replacement Exercises";
const SPINAL = "Pre-Surgical Spinal Procedure Exercises";
const MIND = "Pre-Surgical Mindfulness and Activity Tracker";

const CARE_TEAM =
  "Your care team will instruct you on those exercises that best fit your particular health and safety needs.";

const jointExercises = [
  {
    title: "Strength Training Exercises",
    // Unbranded copy for the demo (TO-1808), in the abj-demo S3 folder, so the pdf2026/thumb2026 helpers don't apply
    pdf: "https://orbit-image-video-bucket.s3.us-east-2.amazonaws.com/pdf/2026/abj-demo/Joint-Replacement-Strength-Training-Exercises.pdf",
    thumb: "https://orbit-image-video-bucket.s3.us-east-2.amazonaws.com/pdf/2026/abj-demo/Joint-Replacement-Strength-Training.jpg",
    description: `Exercises for improving surgical outcomes. ${CARE_TEAM}`,
    exercises: [
      "Abdominal Bracing",
      "Single Knee Press",
      "Partial Bridge/Bridge",
      "Wall Squats with Exercise Ball",
      "Mini Squat",
      "Gluteal Set – Laying Down",
      "Quad Set – Isometric Quads",
      "Short Arc Quads (SAQs)",
      "Standing Hip Abduction",
      "Hip Extension – Standing",
      "Hip Flexion – Standing Straight Leg Raise (SLR)",
      "Straight Leg Raise (SLR)",
      "Knee Extension – Self-Assisted",
      "Knee Extension – Long Arc Quad (LAQ)",
    ],
  },
  {
    title: "Flexibility Exercises",
    pdf: pdf2026(JOINT, "Joint Replacement Flexibility Exercises.pdf"),
    thumb: thumb2026("Pre-Surgical Joint Replacement Flexibility.jpg"),
    description: `Exercises to improve your flexibility. ${CARE_TEAM}`,
    exercises: [
      "Standing Hip Flexor Lunge",
      "Hamstring Stretch",
      "Quadriceps Stretch",
      "Calf Stretch",
      "Heel Slides",
      "Lateral Stretch/Side Bends",
    ],
  },
  {
    title: "Post-Surgery Aquatic Exercise Guide",
    pdf: pdf2026(JOINT, "Joint Replacement Post-Surgery Aquatic Exercise Guide.pdf"),
    thumb: thumb2026("Pre-Surgical Joint Replacement Aquatic Exercise Guide.jpg"),
    description: `Aquatic exercise is great for patients who have had joint replacement surgery. Usually, patients can start pool activities 6-8 weeks after their surgery. ${CARE_TEAM}`,
    exercises: [
      "Squats",
      "Hip Abduction/Adduction",
      "Hip Flexion/Extension",
      "Knee Lift/Kick",
      "Hamstring Curls",
      "Underwater Cycling",
    ],
  },
];

const spinalExercises = [
  {
    title: "Strength Training Exercises",
    pdf: pdf2026(SPINAL, "Spinal Procedure Strength Training Exercises.pdf"),
    thumb: thumb2026("Pre-Surgical Spinal Procedure Strength Training.jpg"),
    description: `Exercises for improving surgical outcomes. ${CARE_TEAM}`,
    // 8 per the PDF (Jim, TO-1717): Quadruped and Cat/Cow are separate items;
    // the Figma merges them into one line.
    exercises: [
      "Abdominal Bracing",
      "Alternating Leg Marching",
      "Single Knee Press",
      "Partial Bridge/Bridge",
      "Quadruped",
      "Cat/Cow",
      "Prone Alternating Arm and Leg",
      "Wall Squats with Exercise Ball",
    ],
  },
  {
    title: "Advanced Strength Training Exercises",
    pdf: pdf2026(SPINAL, "Spinal Procedure Advanced Strength Training Exercises.pdf"),
    thumb: thumb2026("Pre-Surgical Spinal Procedure Advanced Strength Training.jpg"),
    description: `Advanced exercises for improving surgical outcomes. ${CARE_TEAM}`,
    exercises: [
      "Bridge with Exercise Ball",
      "Prone Alternating Arms/Legs with Exercise Ball",
      "Partial Sit Ups",
      "Quadruped or Bird Dog Variations",
      "Mini Squat",
      "Squats",
    ],
  },
  {
    title: "Flexibility Exercises",
    pdf: pdf2026(SPINAL, "Spinal Procedure Flexibility Exercises.pdf"),
    thumb: thumb2026("Pre-Surgical Spinal Procedure Flexibility.jpg"),
    description: `Exercises to improve your flexibility. ${CARE_TEAM}`,
    exercises: [
      "Standing Hip Flexor Stretch",
      "Standing Hamstring Stretch",
      "Quadriceps Stretch",
      "Corner Chest/Back Stretch",
      "SGIS Right (for right-sided pain/shift correction)",
      "SGIS Left (for left-sided pain/shift correction)",
      "Doorway Lat Stretch",
    ],
  },
];

const mindfulnessPdf = pdf2026(MIND, "Mindfulness and Breathing Tools for Pain Management.pdf");
const mindfulnessThumb = thumb2026("Pre-Surgical Mindfulness and Breathing Tools.jpg");
const trackerPdf = pdf2026(MIND, "Lifestyle Medicine Activity Tracker.pdf");
const trackerThumb = thumb2026("Pre-Surgical Lifestyle Medicine Activity Tracker.jpg");
// Header banner (Figma 206:350), uploaded by Jim 10/7 to the shared jpgs/ folder.
const bannerImage = thumb2026("Pre-Surgical Exercises Banner.jpg");

// Text stays on the L99 lego's 18px scale (how the site renders Figma's 22px
// copy). The Figma link is 18px against 22px titles, so on this scale it's
// ~15px, sitting directly under its title. The lego forces `p a` to weight
// 300 !important and italicizes its paragraphs, so the bold upright link text
// is set on an inner span, which those rules don't target.
const pdfLinkTextStyle = { fontSize: "15px", fontWeight: 700, fontStyle: "normal" };

// No lego styles <ol>, so the numbered steps get the L99 paragraph type
// (18px Lato Light, 25px line height). Not italic, per Figma.
const numberedStepsStyle = {
  fontFamily: '"Lato", sans-serif',
  fontSize: "18px",
  fontWeight: 300,
  lineHeight: "25px",
  color: "#000",
};

// The card asks for jump links that land "a short offset" above each section.
// HashLink uses scrollIntoView, which honors scroll-margin-top on the target.
const anchorOffsetStyle = { scrollMarginTop: "24px" };

// Figma: 1030px text box at 22px ≈ 843px at the lego's 18px; 800px lands the
// line breaks where the Figma has them. Shared by both intro paragraphs.
const narrowParagraphStyle = { maxWidth: "800px", marginLeft: "auto", marginRight: "auto" };

const PdfLink = ({ href, style }) => (
  <p style={style}>
    <a target="_blank" rel="noreferrer" className="blueLinkfont18" href={href}>
      <span style={pdfLinkTextStyle}>click to download PDF</span>
    </a>
  </p>
);

// Renders the existing L99 "Resource and Description" lego with an L53 bullet
// list (smallFont, 18px to match the copy) inside its contentContainer.
// The thumbnail links to the PDF, so its alt is the link text: it names the
// patient group (joint and spinal titles repeat) and says it opens a PDF.
const ExerciseResource = ({ group, title, pdf, thumb, description, exercises, legoMargin }) => (
  <div className={`lego l099${legoMargin ? ` ${legoMargin}` : ""}`}>
    <div className="legoContainer">
      <div className="imgContainer">
        <a target="_blank" rel="noreferrer" href={pdf}>
          <img className="" alt={`${group} ${title} (PDF)`} src={thumb} />
        </a>
      </div>
      <div className="contentContainer">
        <p className="descriptionHeader" style={{ marginBottom: 0 }}>{title}</p>
        <PdfLink href={pdf} />
        <p>{description}</p>
        <p>
          <span className="boldItalic">
            This PDF includes easy-to-follow instructions for:
          </span>
        </p>
        {/* L53 Bullet List — clearLeft: the global `contentPage section p`
            rule floats paragraphs left, so the list must clear them. */}
        <div className="lego l053 smallFont clearLeft">
          <ul className="w-100">
            {exercises.map((exercise) => (
              <li className="w-100" key={exercise}>
                {exercise}
              </li>
            ))}
          </ul>
        </div>
        {/* End L53 Bullet List */}
      </div>
    </div>
  </div>
);

// The last block before an l066 divider gets no legoMargin (divider adds its own).
const renderResources = (resources, group) =>
  resources.map((resource, i) => (
    <ExerciseResource
      key={resource.pdf}
      {...resource}
      group={group}
      legoMargin={i < resources.length - 1 ? "legoMargin5" : ""}
    />
  ));

// Grey section panel with a colored top bar, per Figma (10025:113 / 10025:120).
// No lego covers this, so it's page-local. The id sits on the panel itself so
// the direction buttons scroll to the top of the bar.
const ExercisePanel = ({ id, title, barColor, barWidth, className = "", children }) => (
  <div
    id={id}
    className={className}
    style={{
      ...anchorOffsetStyle,
      display: "flow-root",
      backgroundColor: "#F5F3F3",
      borderTop: `${barWidth}px solid ${barColor}`,
      padding: "28px 0 32px",
    }}
  >
    <div className="lego l004 legoMargin3">
      <h2 className="subHeadAllCaps">{title}</h2>
    </div>
    {children}
  </div>
);

const LivingWellCenter6_1_PreSurgical = () => {
  return (
    <div>
      <div className="w-100 legosNewBuild livingWellCenterHeader">
        <section className="mb-0">
          {/* L6 Background image header bar */}
          <div className="lego l006 legoMargin5">
            <div
              className="backgroundImage"
              style={{
                backgroundImage: "url(" + bannerImage + ")",
              }}
            >
              <div
                className="transparentBarTypeContainer abjHeader62a751"
                style={{
                  backgroundColor: "#8DCA64",
                }}
              >
                &nbsp;&nbsp;
              </div>
              <h2 className="l6SubheadWithBackground">Pre-Surgical Exercises</h2>
            </div>
          </div>
          {/* End L6 Background image header bar */}
        </section>
        <Container>
          <div className="w-100">
            <div className="col-12 m-auto">
              <div className="whiteBackground floatLeft clearLeft legosNewBuild livingWellCenterHeader">
                <section>
                  {/* L00jumpNav - section jump-link navigation */}
                  <div className="lego l00jumpNav greenNav wideNav legoMargin4">
                    <nav className="jumpNavContainer">
                      <Link smooth replace className="jumpNavButton" to="#joint-replacement-exercises">
                        Joint Replacement Exercises
                      </Link>
                      <Link smooth replace className="jumpNavButton" to="#spinal-procedure-exercises">
                        Spinal Procedure Exercises
                      </Link>
                      <Link smooth replace className="jumpNavButton" to="#mindfulness-breathing">
                        Mindfulness/Breathing for Pain Management
                      </Link>
                      <Link smooth replace className="jumpNavButton" to="#activity-tracker">
                        Lifestyle Medicine Activity Tracker
                      </Link>
                    </nav>
                  </div>
                  {/* End L00jumpNav */}

                  {/* L4 Subhead Upper */}
                  <div className="lego l004 legoMargin3" id="pre-surgical-exercises">
                    <h2 className="subHeadAllCaps">PRE-SURGICAL EXERCISES</h2>
                  </div>
                  {/* End L4 Subhead Upper */}

                  {/* L14 Paragraph — narrowed to wrap like the Figma */}
                  <div className="lego l00paragraph legoMargin4" style={narrowParagraphStyle}>
                    <p>
                      We help you prepare
                      for surgery by focusing on lifestyle habits that can
                      support your recovery, including expert-led training on
                      movement and exercise.
                    </p>
                  </div>
                  {/* End L14 Paragraph */}

                  {/* Directional buttons — L00jumpNav with the directionNav
                      variant (two large rounded buttons; pending Jim's OK). */}
                  <div className="lego l00jumpNav greenNav directionNav legoMargin5">
                    <nav className="jumpNavContainer">
                      <Link smooth replace className="jumpNavButton oliveGreen" to="#joint-replacement-exercises">
                        <span className="jumpNavLead">Exercises for</span>
                        <span className="jumpNavMain">Joint Replacement Patients</span>
                      </Link>
                      <Link smooth replace className="jumpNavButton darkGreen" to="#spinal-procedure-exercises">
                        <span className="jumpNavLead">Go to Exercises for</span>
                        <span className="jumpNavMain">
                          Spinal Procedure{" "}
                          <span className="text-nowrap">
                            Patients
                            <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
                          </span>
                        </span>
                      </Link>
                    </nav>
                  </div>
                  {/* End directional buttons */}

                  {/* ===== EXERCISES FOR JOINT REPLACEMENT PATIENTS ===== */}
                  <ExercisePanel
                    id="joint-replacement-exercises"
                    title="EXERCISES FOR JOINT REPLACEMENT PATIENTS"
                    barColor="#86A45B"
                    barWidth={17}
                    className="legoMargin5"
                  >
                    {renderResources(jointExercises, "Joint Replacement")}
                  </ExercisePanel>

                  {/* ===== EXERCISES FOR SPINAL PROCEDURE PATIENTS ===== */}
                  <ExercisePanel
                    id="spinal-procedure-exercises"
                    title="EXERCISES FOR SPINAL PROCEDURE PATIENTS"
                    barColor="#238D6C"
                    barWidth={15}
                  >
                    {renderResources(spinalExercises, "Spinal Procedure")}
                  </ExercisePanel>

                  {/* ===== MINDFULNESS AND BREATHING TOOLS ===== */}
                  {/* L66 Divider Rule */}
                  <div className="lego l066"><hr className="horizonalRule" /></div>
                  {/* End L66 Divider Rule */}

                  <div
                    className="lego l004 legoMargin3"
                    id="mindfulness-breathing"
                    style={anchorOffsetStyle}
                  >
                    <h2 className="subHeadAllCaps">
                      FOR ALL PATIENTS
                      <br />
                      MINDFULNESS AND BREATHING TOOLS FOR PAIN MANAGEMENT
                    </h2>
                  </div>

                  {/* L99 Resource and Description - Mindfulness (steps inline + PDF) */}
                  <div className="lego l099 legoMargin5">
                    <div className="legoContainer">
                      <div className="imgContainer">
                        <a target="_blank" rel="noreferrer" href={mindfulnessPdf}>
                          <img
                            className=""
                            alt="Mindfulness and Breathing Tools for Pain Management (PDF)"
                            src={mindfulnessThumb}
                          />
                        </a>
                        {/* Figma puts this block's link under the thumbnail */}
                        <PdfLink
                          href={mindfulnessPdf}
                          style={{ marginTop: "12px", textAlign: "left", whiteSpace: "nowrap" }}
                        />
                      </div>
                      <div className="contentContainer">
                        <p className="descriptionHeader">Mindfulness Body Scan Exercise</p>
                        <ol className="clearLeft" style={numberedStepsStyle}>
                          <li>
                            Lie on your back or in any comfortable position with
                            your body fully extended.
                          </li>
                          <li>
                            Close your eyes and pay attention to your breathing.
                            Feel your belly go up when you inhale and go down when
                            you exhale.
                          </li>
                          <li>
                            Focus on your left foot. Notice any sensations,
                            including pain. Try to sink a little more into the
                            floor every time you exhale.
                          </li>
                          <li>
                            If your mind wanders, notice where it has gone and
                            gently bring your attention back to your foot.
                          </li>
                          <li>
                            If you feel pain, acknowledge it and any thoughts or
                            emotions that come with it. Breathe through it gently.
                            By observing the discomfort, try to help your body
                            relax. Do not expect the pain to go away; just pay
                            attention to it.
                          </li>
                          <li>
                            Slowly let go of focusing on your left foot
                            completely, even if the pain is still there or has
                            gotten worse, and move to the left ankle and repeat
                            the process.
                          </li>
                          <li>
                            Continue this process slowly and patiently throughout
                            your whole body.
                          </li>
                        </ol>

                        <p className="descriptionHeader">Diaphragmatic Breathing Exercise</p>
                        <p>
                          You can do this breathing exercise while sitting or
                          standing. Practice it 3-4 times a day for 5-10 minutes,
                          and gradually increase the time.
                        </p>
                        <ol className="clearLeft" style={numberedStepsStyle}>
                          <li>
                            Place one hand on your upper chest and the other on
                            your abdomen (just below your rib cage).
                          </li>
                          <li>Relax your shoulders.</li>
                          <li>
                            Tighten your abdominal muscles. The hand on your upper
                            chest should stay as still as possible.
                          </li>
                          <li>
                            Inhale through your nose for 2 counts and exhale very
                            slowly through pursed lips for 4 counts, watching your
                            belly move up as it fills with air and move down as it
                            releases air.
                          </li>
                        </ol>
                      </div>
                    </div>
                  </div>
                  {/* End L99 Resource and Description - Mindfulness */}

                  {/* L4 Subhead Upper */}
                  <div className="lego l004 legoMargin3" id="mindfulness-apps">
                    <h2 className="subHeadAllCaps">FREE MINDFULNESS AND MEDITATION APPS</h2>
                  </div>
                  {/* End L4 Subhead Upper */}
                  <LegoL99AData cards={mindfulnessAppsCards} className="noTitleUnderline" />

                  {/* ===== LIFESTYLE MEDICINE ACTIVITY TRACKER ===== */}
                  {/* L66 Divider Rule — standard 72px each side (Figma ~80/74).
                      LegoL99AData gives the last card a 72px legoMargin5, so
                      the wrapper's -72px top keeps it from doubling up. */}
                  <div className="lego l066" style={{ marginTop: "-72px" }}>
                    <hr className="horizonalRule" />
                  </div>
                  {/* End L66 Divider Rule */}

                  <div
                    className="lego l004 legoMargin3"
                    id="activity-tracker"
                    style={anchorOffsetStyle}
                  >
                    <h2 className="subHeadAllCaps">LIFESTYLE MEDICINE ACTIVITY TRACKER</h2>
                  </div>

                  {/* L14 Paragraph — narrowed to wrap like the Figma (breaks
                      after "daily activities,") */}
                  <div className="lego l00paragraph legoMargin3" style={narrowParagraphStyle}>
                    <p>
                      As part of your plan to get and stay healthier, print out
                      this Activity Tracker to log your daily activities, minutes
                      spent and how you felt doing them.
                    </p>
                  </div>
                  {/* End L14 Paragraph */}

                  {/* L99B Resource and Description Image Top and Centered New */}
                  <div className="lego l099B legoMargin5">
                    <div className="legoContainer">
                      {/* Figma thumbnail is 374px wide at its 22px text scale;
                          ~306px keeps that proportion at the lego's 18px. */}
                      <div className="imgContainer" style={{ width: "306px", maxWidth: "100%" }}>
                        <a target="_blank" rel="noreferrer" href={trackerPdf}>
                          <img
                            className=""
                            alt="Lifestyle Medicine Activity Tracker (PDF)"
                            src={trackerThumb}
                          />
                        </a>
                      </div>
                      <div className="contentContainer">
                        <p className="descriptionHeader">Lifestyle Medicine Activity Tracker</p>
                        <p>
                          Click image or{" "}
                          <a
                            target="_blank"
                            rel="noreferrer"
                            className="blueLinkfont18"
                            href={trackerPdf}
                          >
                            <span style={{ fontWeight: 700, fontStyle: "normal" }}>this link</span>
                          </a>{" "}
                          to view.
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* End L99B Resource and Description Image Top and Centered New */}

                  {/* L28 Back To Top */}
                  <div className="lego l028 backToTop">
                    <div className="w-100">
                      <p>
                        <a
                          className="specialUseBackToTop"
                          onClick={() =>
                            window.scrollTo({ top: 0, behavior: "smooth" })
                          }
                        >
                          BACK TO TOP{" "}
                        </a>
                      </p>
                    </div>
                  </div>
                  {/* End L28 Back To Top */}
                </section>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default LivingWellCenter6_1_PreSurgical;
